import {Component, OnDestroy, OnInit, signal} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../classes/user.class';
import {UserService} from '../../services/user/user.service';
import {MatFormField, MatInput} from '@angular/material/input';
import {MatIconButton} from '@angular/material/button';
import {FieldErrorComponent} from '../../field-error/field-error.component';
import {Utils} from '../../utils.class';
import {CodaltComponent} from '../../codalt.component';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {Routenames} from '../../classes/route-name.enum';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {MatIcon} from '@angular/material/icon';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {NgSelectComponent} from '@ng-select/ng-select';
import {Roles} from '../../services/auth/roles';
import {Role} from '../../classes/auth/role.class';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Title} from '@angular/platform-browser';

@Component({
    selector: 'app-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    imports: [
        MatInput,
        MatFormField,
        FormsModule,
        ReactiveFormsModule,
        FieldErrorComponent,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        BreadcrumbComponent,
        MatIcon,
        MatIconButton,
        NgSelectComponent
    ]
})
export class UserEditComponent extends CodaltComponent implements OnInit, OnDestroy {
    saving = false;
    title = '';

    readonly form = signal<FormGroup | null>(null);
    readonly users = signal<User[]>([]);

    minDate: Date;
    rolesMap: Map<Roles, Role>;
    roles: Role[];

    constructor(
        private activatedRoute: ActivatedRoute,
        private authorisationService: AuthorisationService,
        private confirmDialog: ConfirmDialogService,
        private userService: UserService,
        private router: Router,
        private titleService: Title,
    ) {
        super();
        this.titleService.setTitle(`Gebruikers ${this.environment.titleAppend}`);
    }

    ngOnInit() {
        this.subscriptions.add(
            this.authorisationService.getRolesMap().subscribe(rolesMap => {
                this.rolesMap = rolesMap;
                this.roles = Array.from(rolesMap.values()).filter(r => !r.restricted || AuthorisationService.hasRole(Roles.Management));

                this.subscriptions.add(
                    this.activatedRoute.params.subscribe((params: {id}) => {
                        if (params.id === 'new') {
                            this.createForm(new User());
                        } else {
                            this.subscriptions.add(
                                this.userService.getUser(+params.id).subscribe({
                                    next: res => this.createForm(res),
                                    error: err => console.error(err)
                                })
                            );
                        }
                    })
                );
            })
        );

    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            const user = new User();
            Object.assign(user, this.form().value);
            user.part_time_rate = parseFloat((this.form()?.get('part_time_rate').value / 100).toFixed(2));
            this.subscriptions.add(
                this.userService.saveUser(user).subscribe({
                    next: () => this.router.navigate([Routenames.users]),
                    error: err => console.error(err)

                })
            );
        }
    }

    deleteUser(event) {
        event.preventDefault();
        event.stopPropagation();

        const user = new User();
        Object.assign(user, this.form().value);

        this.confirmDialog.confirm(
            'Verwijderen',
            `Weet u zeker dat u de gebruiker ${user.name} wilt verwijderen?`,
            'Ja',
            'nee'
        )
            .then(() => {
                this.userService.deleteUser(user.id).then(() => {
                    this.router.navigate([Routenames.users]);
                });
            })
            .catch(() => {
                console.log('user not deleted')
            });
    }

    private createForm(user: User) {
        this.crumbs = [
            {name: 'Gebruikers', route: Routenames.users},
            {name: `Gebruiker : ${user?.name || 'new'}`, route: `${Routenames.users}/${user?.id || 'new'}`}
        ];

        this.title = user.id ? 'aanpassen' : 'toevoegen';
        this.form.set(new FormGroup({
            id: new FormControl(user.id),
            name: new FormControl(user.name, [Validators.required, Validators.minLength(3)]),
            email: new FormControl(user.email, [Validators.required, Validators.email]),
            hourly_rate: new FormControl(user.hourly_rate),
            part_time_rate: new FormControl(user.part_time_rate * 100),
            employment_start: new FormControl(user.employment_start),
            employment_end: new FormControl(user.employment_end),
            period_costs: new FormControl(user.period_costs),
            roles: new FormControl(user.roles ? user.roles.map(r => r.key) : []),
            updated_at: new FormControl(user.updated_at)
        }));
    }
}
